import React from 'react';
import { formatDateEs } from '../../../utils';
import './TreatmentPlansTable.css';

const TreatmentPlansTable = ({
  treatmentPlans,
  onView,
  onEdit,
  onDelete,
  onPrint,
  onEmail,
  searchQuery,
  onStatusChange,
  onRestore,
  onGenerateInvoice,
  readOnly = false
}) => {

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const renderExpirationStatus = (plan) => {
    if (!plan.expirationDate) {
      return <span className="expiration-status no-expiration">Sin fecha</span>;
    }

    const expirationDate = new Date(plan.expirationDate);
    const now = new Date();
    const diffTime = expirationDate - now;
    const computedRemainingDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const remainingDays = Number.isFinite(plan.remainingDays)
      ? plan.remainingDays
      : Math.max(0, computedRemainingDays);
    const isExpired = typeof plan.isExpired === 'boolean'
      ? plan.isExpired
      : expirationDate < now;

    if (isExpired) {
      return (
        <span className="expiration-status expired">
          <i className="fas fa-exclamation-triangle"></i>
          Expirado
        </span>
      );
    } else if (remainingDays <= 7) {
      return (
        <span className="expiration-status warning">
          <i className="fas fa-clock"></i>
          {remainingDays} día{remainingDays !== 1 ? 's' : ''}
        </span>
      );
    } else {
      return (
        <span className="expiration-status valid">
          <i className="fas fa-check-circle"></i>
          {remainingDays} días
        </span>
      );
    }
  };

  return (
    <div className="treatment-plans-table-container">
      {searchQuery && (
        <div className="search-results-info">
          <span className="results-count">
            {treatmentPlans.length} resultado{treatmentPlans.length !== 1 ? 's' : ''} encontrado{treatmentPlans.length !== 1 ? 's' : ''}
          </span>
          <span className="search-term">para "{searchQuery}"</span>
        </div>
      )}
      
      {/* Desktop Table View */}
      <table className="treatment-plans-table">
        <thead>
          <tr>
            <th className="col-name">Nombre del Plan</th>
            <th className="col-start-date">Fecha de Inicio</th>
            <th className="col-doctor">Dentista Responsable</th>
            <th className="col-created">Fecha Creado</th>
            <th className="col-cost">Costo</th>
            <th className="col-status">Estado</th>
            <th className="col-validity">Validez</th>
            <th className="col-billed">Facturado</th>
            <th className="col-actions">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {treatmentPlans.map((plan) => {
            const effectiveStatus = plan.status === 'active' ? 'quoted' : plan.status;
            const canGenerateInvoice = ['accepted', 'in_progress', 'paused', 'completed'].includes(effectiveStatus);
            return (
            <tr key={plan.id} className={`treatment-plan-row ${readOnly ? 'read-only-row' : ''}`}>
              <td className="plan-name col-name">
                <div className="name-cell">
                  <div className="plan-icon">
                    <i className="fas fa-clipboard-list"></i>
                  </div>
                  <span>{plan.name}</span>
                </div>
              </td>
              <td className="col-start-date">{formatDateEs(plan.date)}</td>
              <td className="col-doctor">{plan.doctorName || 'Sin asignar'}</td>
              <td className="col-created">{formatDateEs(plan.createdAt)}</td>
              <td className="col-cost"><strong>{formatCurrency(plan.totalCost)}</strong></td>
              <td className="col-status">
                <select
                  value={effectiveStatus}
                  onChange={(e) => onStatusChange(plan.id, e.target.value)}
                  className={`status-select ${effectiveStatus}`}
                  disabled={readOnly}
                >
                  <option value="quoted">Presupuestado</option>
                  <option value="accepted">Aceptado</option>
                  <option value="in_progress">En Curso</option>
                  <option value="paused">Pausado</option>
                  <option value="completed">Completado</option>
                  <option value="rejected">Rechazado</option>
                </select>
              </td>
              <td className="expiration-cell col-validity">
                {effectiveStatus === 'quoted' ? renderExpirationStatus(plan) : '—'}
              </td>
              <td className="col-billed">{plan.invoicedBySat ? 'Sí' : 'No'}</td>
              <td className="actions-cell col-actions">
                <div className="table-actions">
                  <button
                    className="btn btn-primary btn-icon btn-sm"
                    onClick={() => onView(plan)}
                    title="Ver"
                  >
                    <i className="fas fa-eye" aria-hidden="true"></i>
                  </button>
                  <button
                    className="btn btn-success btn-icon btn-sm"
                    onClick={() => onPrint(plan)}
                    title="Imprimir"
                  >
                    <i className="fas fa-print" aria-hidden="true"></i>
                  </button>
                  {onEmail && (
                  <button
                    className="btn btn-info btn-icon btn-sm"
                    onClick={() => onEmail(plan)}
                    title="Enviar por correo"
                  >
                    <i className="fas fa-envelope" aria-hidden="true"></i>
                  </button>
                  )}
                  <button
                    className="btn btn-warning btn-icon btn-sm"
                    onClick={() => onGenerateInvoice && onGenerateInvoice(plan)}
                    title="Generar factura"
                    disabled={!canGenerateInvoice || readOnly}
                  >
                    <i className="fas fa-file-invoice-dollar" aria-hidden="true"></i>
                  </button>
                  {readOnly && (
                    <button
                      className="restore-button"
                      onClick={() => onRestore && onRestore(plan)}
                      title="Restaurar"
                    >
                      <i className="fas fa-undo"></i>
                      Restaurar
                    </button>
                  )}
                </div>
              </td>
            </tr>
          );
          })}
        </tbody>
      </table>

      {/* Mobile Cards View */}
      <div className="treatment-plans-mobile-cards">
        {treatmentPlans.map((plan) => {
          const effectiveStatus = plan.status === 'active' ? 'quoted' : plan.status;
          const canGenerateInvoice = ['accepted', 'in_progress', 'paused', 'completed'].includes(effectiveStatus);
          return (
          <div key={plan.id} className="treatment-plan-card">
            <div className="plan-card-header">
              <div className="plan-card-title">
                <div className="plan-icon">
                  <i className="fas fa-clipboard-list"></i>
                </div>
                <span>{plan.name}</span>
              </div>
            </div>
            <div className="plan-card-content">
              <div className="plan-card-row">
                <span className="plan-card-label">Fecha de Inicio:</span>
                <span className="plan-card-value">{formatDateEs(plan.date)}</span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Dentista:</span>
                <span className="plan-card-value">{plan.doctorName || 'Sin asignar'}</span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Fecha Creado:</span>
                <span className="plan-card-value">{formatDateEs(plan.createdAt)}</span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Costo:</span>
                <span className="plan-card-value"><strong>{formatCurrency(plan.totalCost)}</strong></span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Estado:</span>
                <span className="plan-card-value">
                  <select
                    value={effectiveStatus}
                    onChange={(e) => onStatusChange(plan.id, e.target.value)}
                    className={`status-select ${effectiveStatus}`}
                    disabled={readOnly}
                  >
                    <option value="quoted">Presupuestado</option>
                    <option value="accepted">Aceptado</option>
                    <option value="in_progress">En Curso</option>
                    <option value="paused">Pausado</option>
                    <option value="completed">Completado</option>
                    <option value="rejected">Rechazado</option>
                  </select>
                </span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Validez:</span>
                <span className="plan-card-value">
                  {effectiveStatus === 'quoted' ? renderExpirationStatus(plan) : '—'}
                </span>
              </div>
              <div className="plan-card-row">
                <span className="plan-card-label">Facturado:</span>
                <span className="plan-card-value">{plan.invoicedBySat ? 'Sí' : 'No'}</span>
              </div>
            </div>
            <div className="plan-card-actions">
              <button
                className="btn btn-primary btn-sm"
                onClick={() => onView(plan)}
                title="Ver"
              >
                <i className="fas fa-eye"></i>
                <span>Ver</span>
              </button>
              <button
                className="btn btn-success btn-sm"
                onClick={() => onPrint(plan)}
                title="Imprimir"
              >
                <i className="fas fa-print"></i>
                <span>Imprimir</span>
              </button>
              {onEmail && (
              <button
                className="btn btn-info btn-sm"
                onClick={() => onEmail(plan)}
                title="Enviar por correo"
              >
                <i className="fas fa-envelope"></i>
                <span>Correo</span>
              </button>
              )}
              <button
                className="btn btn-warning btn-sm"
                onClick={() => onGenerateInvoice && onGenerateInvoice(plan)}
                title="Generar factura"
                disabled={!canGenerateInvoice || readOnly}
              >
                <i className="fas fa-file-invoice-dollar"></i>
                <span>Facturar</span>
              </button>
              {readOnly && (
                <button
                  className="restore-button-mobile"
                  onClick={() => onRestore && onRestore(plan)}
                  title="Restaurar"
                >
                  <i className="fas fa-undo"></i>
                  <span>Restaurar</span>
                </button>
              )}
            </div>
          </div>
        );
        })}
      </div>
    </div>
  );
};

export default TreatmentPlansTable;
